import React, { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  FormControlLabel, 
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  RadioGroup,
  Radio
} from '@mui/material';
import { API_URL } from '../../config/api';

const validationSchema = yup.object({
  // Basic Information
  employeeName: yup.string().required('Employee name is required'),
  designation: yup.string().required('Designation is required'),
  personalEmail: yup.string().email('Enter a valid email'),
  doj: yup.date().required('Date of joining is required'),
  lwd: yup.date().required('Last working day is required'),
  resignationDate: yup.date().required('Resignation date is required'),
  reportingManager: yup.string().required('Reporting manager is required'),
  department: yup.string().required('Department is required'),
  
  // Decision Timeline
  decisionTimeline: yup.string().required('Please select when you began making your decision'),
  decisionTimelineOther: yup.string().when('decisionTimeline', {
    is: 'other',
    then: () => yup.string().required('Please specify other timeline'),
    otherwise: () => yup.string()
  }),
  
  // Resignation Reasons
  resignationReasons: yup.array().min(1, 'Please select at least one reason'),
  resignationReasonsOther: yup.string().when('resignationReasons', {
    is: (reasons) => reasons && reasons.includes('other'),
    then: () => yup.string().required('Please explain other reason'),
    otherwise: () => yup.string()
  }),
  
  // Specific Event
  specificEvent: yup.string().required('Please indicate if there was a specific event'),
  specificEventExplanation: yup.string().when('specificEvent', {
    is: 'yes',
    then: () => yup.string().required('Please explain the specific event'),
    otherwise: () => yup.string()
  }),
  discussedWithManager: yup.string().when('specificEvent', {
    is: 'yes',
    then: () => yup.string().required('Please indicate if you discussed with manager'),
    otherwise: () => yup.string()
  }),
  
  // Ratings (1-5 scale)
  trainingRating: yup.number().min(1).max(5).required('Please rate training received'),
  supervisorRelationshipRating: yup.number().min(1).max(5).required('Please rate supervisor relationship'),
  colleagueRelationshipRating: yup.number().min(1).max(5).required('Please rate colleague relationship'),
  salaryRating: yup.number().min(1).max(5).required('Please rate salary'),
  benefitsRating: yup.number().min(1).max(5).required('Please rate benefits'),
  workloadRating: yup.number().min(1).max(5).required('Please rate workload'),
  satisfactionRating: yup.number().min(1).max(5).required('Please rate overall satisfaction'),
  
  // Benefits Comments
  benefitsComments: yup.string(),
  
  // Problems Encountered
  problemsEncountered: yup.string().required('Please indicate if you encountered problems'),
  problemsExplanation: yup.string().when('problemsEncountered', {
    is: 'yes',
    then: () => yup.string().required('Please explain the problems encountered'),
    otherwise: () => yup.string()
  }),
  
  // Enjoyment
  enjoyedMost: yup.string().required('Please describe what you enjoyed most'),
  enjoyedLeast: yup.string().required('Please describe what you enjoyed least'),
  
  // Recommendations
  wouldRecommend: yup.string().required('Please indicate if you would recommend Divergent Insights'),
  wouldRecommendExplanation: yup.string().when('wouldRecommend', {
    is: 'no',
    then: () => yup.string().required('Please explain why you would not recommend'),
    otherwise: () => yup.string()
  }),
  
  // Reapply
  wouldReapply: yup.string().required('Please indicate if you would reapply'),
  wouldReapplyExplanation: yup.string().when('wouldReapply', {
    is: 'no',
    then: () => yup.string().required('Please explain why you would not reapply'),
    otherwise: () => yup.string()
  }),
  
  // Additional Comments
  additionalComments: yup.string(),
  
  // Handover
  handoverGiven: yup.string().required('Please indicate if handover was given'),
  chargeTakenBy: yup.string().when('handoverGiven', {
    is: 'yes',
    then: () => yup.string().required('Please specify who took charge'),
    otherwise: () => yup.string()
  }),
  
  // Asset Return
  idCardReturned: yup.string().required('Please indicate if ID card was returned'),
  laptopReturned: yup.string().required('Please indicate if laptop was returned'),
  
  // Exit Interview
  
  exitInterviewConductedBy: yup.string().required('Please specify who conducted exit interview'),
  exitInterviewDate: yup.date().required('Exit interview date is required')
});

const ExitInterviewStep = ({ exitFormId, readOnly = false, userIdOverride }) => {
  const { user, getAuthHeaders } = useAuth();
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const effectiveUserId = userIdOverride || user?.id;
  const [userOptions, setUserOptions] = useState([]);

  // Helper function to format date for input fields
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Helper to extract date (YYYY-MM-DD) from ISO datetime
  const extractDateOnly = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Basic Information
      employeeName: user ? `${user.firstName} ${user.lastName}` : '',
      designation: user ? user.jobTitle || '' : '',
      personalEmail: '',
      doj: '',
      lwd: '',
      resignationDate: '',
      reportingManager: '',
      department: user ? user.department || '' : '',
      
      // Decision Timeline
      decisionTimeline: '',
      decisionTimelineOther: '',
      
      // Resignation Reasons
      resignationReasons: [],
      resignationReasonsOther: '',
      
      // Specific Event
      specificEvent: '',
      specificEventExplanation: '',
      discussedWithManager: '',
      
      // Ratings
      trainingRating: 3,
      supervisorRelationshipRating: 3,
      colleagueRelationshipRating: 3,
      salaryRating: 3,
      benefitsRating: 3,
      workloadRating: 3,
      satisfactionRating: 3,
      
      // Benefits Comments
      benefitsComments: '',
      
      // Problems Encountered
      problemsEncountered: '',
      problemsExplanation: '',
      
      // Enjoyment
      enjoyedMost: '',
      enjoyedLeast: '',
      
      // Recommendations
      wouldRecommend: '',
      wouldRecommendExplanation: '',
      
      // Reapply
      wouldReapply: '',
      wouldReapplyExplanation: '',
      
      // Additional Comments
      additionalComments: '',
      
      // Handover
      handoverGiven: '',
      chargeTakenBy: '',
      
      // Asset Return
      idCardReturned: '',
      laptopReturned: '',
      
      // Exit Interview
      exitInterviewConductedBy: '',
      exitInterviewDate: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const url = `${API_URL}/offboarding/exit-form/user/${effectiveUserId}`;
        // Build ISO datetime from date only (time set to 00:00 local)
        let payload = { ...values };
        if (values.exitInterviewDate) {
          const combinedLocal = `${values.exitInterviewDate}T00:00`;
          payload.exitInterviewDateTime = new Date(combinedLocal).toISOString();
        }
        
        const response = await axios.put(url, payload, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });
        
        if (response.status === 201) {
          toast.success('Exit form created successfully!');
          setIsUpdate(true);
        } else {
          toast.success('Exit form updated successfully!');
        }
        console.log('Exit form submitted successfully');

        // Disable system access provisioning after successful exit interview submission
        try {
          const disableAccessResponse = await axios.put(`${API_URL}/access-provisioning/disable/${effectiveUserId}`, {}, {
            headers: getAuthHeaders()
          });
          
          if (disableAccessResponse.data.success) {
            console.log('System access provisioning disabled successfully');
            toast.success('System access has been disabled for this employee');
          }
        } catch (disableError) {
          console.error('Error disabling system access:', disableError);
          // Don't show error to user as exit form was submitted successfully
          // Just log the error for debugging
        }
        
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Failed to submit exit form';
        toast.error(errorMessage);
        console.error('Error submitting exit form:', error);
      } finally {
        setLoading(false);
      }
    }
  });

  // Load all users for dropdowns
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await axios.get(`${API_URL}/users/public`, { headers: getAuthHeaders() });
        setUserOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load users list', e);
        setUserOptions([]);
      }
    };
    loadUsers();
  }, [getAuthHeaders]);

  useEffect(() => {
    if (effectiveUserId) {
      axios.get(`${API_URL}/offboarding/exit-form/user/${effectiveUserId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          const data = response.data;
          if (data.data && data.data.length > 0) {
            const formData = data.data[0];
            const formattedData = {
              ...formData,
              // Preserve designation and department from AuthContext
              designation: user ? user.jobTitle || formData.designation || '' : formData.designation || '',
              department: user ? user.department || formData.department || '' : formData.department || '',
              personalEmail: formData.personalEmail || '',
              doj: formatDateForInput(formData.doj),
              lwd: formatDateForInput(formData.lwd),
              resignationDate: formatDateForInput(formData.resignationDate),
              exitInterviewDate: extractDateOnly(formData.exitInterviewDateTime)
            };
            formik.setValues(formattedData);
            setIsUpdate(true);
          }
          // If no data exists, continue with empty form - this is expected for new users
        })
        .catch(error => {
          // Only show error if it's not a 404 (no form found) or similar "not found" scenarios
          if (error.response && error.response.status !== 404) {
            console.error('Error fetching exit form:', error);
            toast.error('Error loading existing form data');
          }
          // If 404 or no data, just continue with empty form - this is expected for new users
        });
    }
    // eslint-disable-next-line
  }, [effectiveUserId, getAuthHeaders]);

  // Add scroll-to-error functionality
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0 && Object.keys(formik.touched).length > 0) {
      // Find the first error field that has been touched
      const firstErrorField = Object.keys(formik.errors).find(field => formik.touched[field]);
      
      if (firstErrorField) {
        // Handle nested fields (e.g., currentAddress.street)
        const fieldName = firstErrorField.includes('.') ? firstErrorField : firstErrorField;
        const errorElement = document.querySelector(`[name="${fieldName}"]`);
        
        if (errorElement) {
          errorElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          errorElement.focus();
        }
      }
    }
  }, [formik.errors, formik.touched]);

  const handleRatingChange = (field, value) => {
    formik.setFieldValue(field, value);
  };

  const handleResignationReasonChange = (reason) => {
    const currentReasons = formik.values.resignationReasons || [];
    const updatedReasons = currentReasons.includes(reason)
      ? currentReasons.filter(r => r !== reason)
      : [...currentReasons, reason];
    formik.setFieldValue('resignationReasons', updatedReasons);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
          EXIT FORM
        </Typography>
        
        {/* Basic Information Section */}
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee Name"
                name="employeeName"
                value={formik.values.employeeName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                error={formik.touched.employeeName && Boolean(formik.errors.employeeName)}
                helperText={formik.touched.employeeName && formik.errors.employeeName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Designation"
                name="designation"
                value={formik.values.designation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-readOnly': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                error={formik.touched.designation && Boolean(formik.errors.designation)}
                helperText={formik.touched.designation && formik.errors.designation}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="DOJ (Date of Joining)"
                name="doj"
                type="date"
                value={formik.values.doj}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.doj && Boolean(formik.errors.doj)}
                helperText={formik.touched.doj && formik.errors.doj}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="LWD (Last Working Day)"
                name="lwd"
                type="date"
                value={formik.values.lwd}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.lwd && Boolean(formik.errors.lwd)}
                helperText={formik.touched.lwd && formik.errors.lwd}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Resignation Date"
                name="resignationDate"
                type="date"
                value={formik.values.resignationDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.resignationDate && Boolean(formik.errors.resignationDate)}
                helperText={formik.touched.resignationDate && formik.errors.resignationDate}
              />
            </Grid>
            {/* Personal Email - separate row after Resignation Date */}
            <Grid item xs={12}>
              <TextField
                label="Personal Email Id"
                name="personalEmail"
                type="email"
                value={formik.values.personalEmail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.personalEmail && Boolean(formik.errors.personalEmail)}
                helperText={formik.touched.personalEmail && formik.errors.personalEmail}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={formik.touched.reportingManager && Boolean(formik.errors.reportingManager)}>
                <InputLabel>Reporting Manager</InputLabel>
                <Select
                  label="Reporting Manager"
                  name="reportingManager"
                  value={formik.values.reportingManager}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="">Select</MenuItem>
                  {Array.isArray(userOptions) && userOptions.map((u) => (
                    <MenuItem key={u._id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim()}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.reportingManager && formik.errors.reportingManager && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.reportingManager}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                name="department"
                value={formik.values.department}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input.Mui-readOnly': {
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed'
                  }
                }}
                error={formik.touched.department && Boolean(formik.errors.department)}
                helperText={formik.touched.department && formik.errors.department}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Decision Timeline Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            1. At approximately what point in time did you begin making your decision to resign?
          </Typography>
          <RadioGroup
            name="decisionTimeline"
            value={formik.values.decisionTimeline}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="6-9 months ago" control={<Radio />} label="6-9 months ago" />
            <FormControlLabel value="3-5 months ago" control={<Radio />} label="3-5 months ago" />
            <FormControlLabel value="1-2 months ago" control={<Radio />} label="1-2 months ago" />
            <FormControlLabel value="other" control={<Radio />} label="Other" />
          </RadioGroup>
          {formik.touched.decisionTimeline && Boolean(formik.errors.decisionTimeline) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.decisionTimeline}
            </Typography>
          )}
          {formik.values.decisionTimeline === 'other' && (
            <TextField
              label="Please specify"
              name="decisionTimelineOther"
              value={formik.values.decisionTimelineOther}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              sx={{ mt: 1 }}
              required
              error={formik.touched.decisionTimelineOther && Boolean(formik.errors.decisionTimelineOther)}
              helperText={formik.touched.decisionTimelineOther && formik.errors.decisionTimelineOther}
            />
          )}
        </Box>

        {/* Resignation Reasons Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            2. Please indicate reason(s) below, which contributed to your decision to resign your current position?
          </Typography>
          <Grid container spacing={2}>
            {[
              'Salary', 'Family Responsibilities', 'Job Advancement','Personal', 'Dissatisfied/Management',
              ' Benefits', 'Return to School', 'Job Eliminated', 'Relocation',
              'Health Retirement',  'Dissatisfaction with the Manager'
            ].map((reason) => (
              <Grid item xs={12} sm={6} md={4} key={reason}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.resignationReasons?.includes(reason) || false}
                      onChange={() => handleResignationReasonChange(reason)}
                    />
                  }
                  label={reason}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.resignationReasons?.includes('other') || false}
                    onChange={() => handleResignationReasonChange('other')}
                  />
                }
                label="Other"
              />
            </Grid>
          </Grid>
          {formik.values.resignationReasons?.includes('other') && (
            <TextField
              label="Please explain other reason"
              name="resignationReasonsOther"
              value={formik.values.resignationReasonsOther}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              required
              error={formik.touched.resignationReasonsOther && Boolean(formik.errors.resignationReasonsOther)}
              helperText={formik.touched.resignationReasonsOther && formik.errors.resignationReasonsOther}
            />
          )}
        </Box>

        {/* Specific Event Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            3. Was there a specific event or issue that prompted your resignation?
          </Typography>
          <RadioGroup
            name="specificEvent"
            value={formik.values.specificEvent}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.specificEvent && Boolean(formik.errors.specificEvent) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.specificEvent}
            </Typography>
          )}
          {formik.values.specificEvent === 'yes' && (
            <>
              <TextField
                label="Please briefly explain"
                name="specificEventExplanation"
                value={formik.values.specificEventExplanation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 2 }}
                required
                error={formik.touched.specificEventExplanation && Boolean(formik.errors.specificEventExplanation)}
                helperText={formik.touched.specificEventExplanation && formik.errors.specificEventExplanation}
              />
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                If yes, did you discuss this matter with your supervisor/manager? *
              </Typography>
              <RadioGroup
                name="discussedWithManager"
                value={formik.values.discussedWithManager}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
              {formik.touched.discussedWithManager && Boolean(formik.errors.discussedWithManager) && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                  {formik.errors.discussedWithManager}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Ratings Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            On a scale of 1 to 5, with "1" being poor and "5" being outstanding, please rate the following:
          </Typography>
          
          {[
            { field: 'trainingRating', label: '4. Quantity and quality of training received for your position(s) here at Divergent Insights.' },
            { field: 'supervisorRelationshipRating', label: '5. Working relationship with your current supervisor.' },
            { field: 'colleagueRelationshipRating', label: '6. Working relationship with fellow employees.' },
            { field: 'salaryRating', label: '7. Salary for your position.' }
          ].map(({ field, label }) => (
            <Box key={field} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {label}
              </Typography>
              <FormControl>
                <RadioGroup
                  row
                  name={field}
                  value={formik.values[field]}
                  onChange={(e) => handleRatingChange(field, parseInt(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <FormControlLabel
                      key={rating}
                      value={rating}
                      control={<Radio />}
                      label={rating}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))}
          
          {/* Question 8 - Benefits Rating with Comments */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              8. Employee Benefits package.
            </Typography>
            <FormControl>
              <RadioGroup
                row
                name="benefitsRating"
                value={formik.values.benefitsRating}
                onChange={(e) => handleRatingChange('benefitsRating', parseInt(e.target.value))}
              >
                {[1, 2, 3, 4, 5].map((rating) => (
                  <FormControlLabel
                    key={rating}
                    value={rating}
                    control={<Radio />}
                    label={rating}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <TextField
              label="Please offer any comments or suggestions for improvement related to the Benefits package."
              name="benefitsComments"
              value={formik.values.benefitsComments}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          </Box>
          
          {[
            { field: 'workloadRating', label: '9. Overall workload for your position.' },
            { field: 'satisfactionRating', label: '10. Overall satisfaction and enjoyment in your current position.' }
          ].map(({ field, label }) => (
            <Box key={field} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {label}
              </Typography>
              <FormControl>
                <RadioGroup
                  row
                  name={field}
                  value={formik.values[field]}
                  onChange={(e) => handleRatingChange(field, parseInt(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <FormControlLabel
                      key={rating}
                      value={rating}
                      control={<Radio />}
                      label={rating}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          ))}
        </Box>

        {/* Problems Encountered Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            11. Did you encounter any problems in your current position?
          </Typography>
          <RadioGroup
            name="problemsEncountered"
            value={formik.values.problemsEncountered}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.problemsEncountered && Boolean(formik.errors.problemsEncountered) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.problemsEncountered}
            </Typography>
          )}
          {formik.values.problemsEncountered === 'yes' && (
            <TextField
              label="If yes, please briefly comment"
              name="problemsExplanation"
              value={formik.values.problemsExplanation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              required
              error={formik.touched.problemsExplanation && Boolean(formik.errors.problemsExplanation)}
              helperText={formik.touched.problemsExplanation && formik.errors.problemsExplanation}
            />
          )}
        </Box>

        {/* Enjoyment Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            12. What did you enjoy most about your employment with Divergent Insights?
          </Typography>
          <TextField
            name="enjoyedMost"
            value={formik.values.enjoyedMost}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            multiline
            rows={3}
            required
            error={formik.touched.enjoyedMost && Boolean(formik.errors.enjoyedMost)}
            helperText={formik.touched.enjoyedMost && formik.errors.enjoyedMost}
          />
          
          <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
            13. What did you enjoy least about your employment with Divergent Insights?
          </Typography>
          <TextField
            name="enjoyedLeast"
            value={formik.values.enjoyedLeast}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            multiline
            rows={3}
            required
            error={formik.touched.enjoyedLeast && Boolean(formik.errors.enjoyedLeast)}
            helperText={formik.touched.enjoyedLeast && formik.errors.enjoyedLeast}
          />
        </Box>

        {/* Recommendations Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            14. Based upon your experiences here, would you recommend Divergent Insights as a potential employer for your friends, relatives, etc.?
          </Typography>
          <RadioGroup
            name="wouldRecommend"
            value={formik.values.wouldRecommend}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.wouldRecommend && Boolean(formik.errors.wouldRecommend) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.wouldRecommend}
            </Typography>
          )}
          {formik.values.wouldRecommend === 'no' && (
            <TextField
              label="If no, please briefly explain"
              name="wouldRecommendExplanation"
              value={formik.values.wouldRecommendExplanation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              required
              error={formik.touched.wouldRecommendExplanation && Boolean(formik.errors.wouldRecommendExplanation)}
              helperText={formik.touched.wouldRecommendExplanation && formik.errors.wouldRecommendExplanation}
            />
          )}
          
          <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
            15. Would you reapply to Divergent Insights if a future opportunity arose?
          </Typography>
          <RadioGroup
            name="wouldReapply"
            value={formik.values.wouldReapply}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.wouldReapply && Boolean(formik.errors.wouldReapply) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.wouldReapply}
            </Typography>
          )}
          {formik.values.wouldReapply === 'no' && (
            <TextField
              label="If no, please briefly explain"
              name="wouldReapplyExplanation"
              value={formik.values.wouldReapplyExplanation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={3}
              sx={{ mt: 2 }}
              required
              error={formik.touched.wouldReapplyExplanation && Boolean(formik.errors.wouldReapplyExplanation)}
              helperText={formik.touched.wouldReapplyExplanation && formik.errors.wouldReapplyExplanation}
            />
          )}
        </Box>

        {/* Additional Comments Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Please add any additional comments:
          </Typography>
          <TextField
            name="additionalComments"
            value={formik.values.additionalComments}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            multiline
            rows={4}
          />
        </Box>

        {/* Handover Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            16. Handover Given:
          </Typography>
          <RadioGroup
            name="handoverGiven"
            value={formik.values.handoverGiven}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.handoverGiven && Boolean(formik.errors.handoverGiven) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.handoverGiven}
            </Typography>
          )}
          {formik.values.handoverGiven === 'yes' && (
            <>
              <FormControl fullWidth required error={formik.touched.chargeTakenBy && Boolean(formik.errors.chargeTakenBy)} sx={{ mt: 2 }}>
                <InputLabel>Charge taken By</InputLabel>
                <Select
                  label="Charge taken By"
                  name="chargeTakenBy"
                  value={formik.values.chargeTakenBy}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="">Select</MenuItem>
                  {Array.isArray(userOptions) && userOptions.map((u) => (
                    <MenuItem key={u._id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim()}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.chargeTakenBy && formik.errors.chargeTakenBy && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.chargeTakenBy}
                  </Typography>
                )}
              </FormControl>
            </>
          )}
          </Box>

        {/* Asset Return Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Return of Assets:
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  ID/ Business Card:
                </Typography>
                <RadioGroup
                  row
                  name="idCardReturned"
                  value={formik.values.idCardReturned}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Laptop:
                </Typography>
                <RadioGroup
                  row
                  name="laptopReturned"
                  value={formik.values.laptopReturned}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Exit Interview Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Exit Interview Details:
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={formik.touched.exitInterviewConductedBy && Boolean(formik.errors.exitInterviewConductedBy)}>
                <InputLabel>Exit interview conducted by</InputLabel>
                <Select
                  label="Exit interview conducted by"
                  name="exitInterviewConductedBy"
                  value={formik.values.exitInterviewConductedBy}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="">Select</MenuItem>
                  {Array.isArray(userOptions) && userOptions.map((u) => (
                    <MenuItem key={u._id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim()}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.exitInterviewConductedBy && formik.errors.exitInterviewConductedBy && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.exitInterviewConductedBy}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Interview Date"
                name="exitInterviewDate"
                type="date"
                value={formik.values.exitInterviewDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.exitInterviewDate && Boolean(formik.errors.exitInterviewDate)}
                helperText={formik.touched.exitInterviewDate && formik.errors.exitInterviewDate}
              />
            </Grid>
          </Grid>
        </Box>

        {/* F&F Notice */}
        <Box sx={{ mb: 4, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            F&F will be cleared after 45 Days.
          </Typography>
        </Box>
        </fieldset>
        <Divider sx={{ my: 3 }} />
        
        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ minWidth: 150, py: 1.5 }}
            >
              {loading ? 'Submitting...' : (exitFormId ? 'Update Form' : 'Submit Form')}
            </Button>
          </Box>
        )}
      </form>
    </Paper>
  );
};

export default ExitInterviewStep; 